class PastTripsGallery {
  constructor(root) {
    this.root = root;
    this.lightbox = root.querySelector('[data-past-trips-lightbox]');
    this.dialog = this.lightbox?.querySelector('[role="dialog"]');
    this.title = this.lightbox?.querySelector('[data-lightbox-title]');
    this.destination = this.lightbox?.querySelector('[data-lightbox-destination]');
    this.image = this.lightbox?.querySelector('[data-lightbox-image]');
    this.placeholder = this.lightbox?.querySelector('[data-lightbox-placeholder]');
    this.caption = this.lightbox?.querySelector('[data-lightbox-caption]');
    this.thumbs = this.lightbox?.querySelector('[data-lightbox-thumbs]');
    this.previousButton = this.lightbox?.querySelector('[data-lightbox-prev]');
    this.nextButton = this.lightbox?.querySelector('[data-lightbox-next]');
    this.closeButtons = this.lightbox?.querySelectorAll('[data-lightbox-close]') || [];
    this.currentGallery = null;
    this.currentIndex = 0;
    this.previousFocus = null;
    this.hadScrollLock = false;

    if (!this.lightbox || !this.dialog) return;

    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.bindEvents();
  }

  bindEvents() {
    this.root.querySelectorAll('[data-gallery-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this.open(trigger.dataset.galleryTrigger);
      });
    });

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.previousButton?.addEventListener('click', () => this.showPrevious());
    this.nextButton?.addEventListener('click', () => this.showNext());
  }

  open(galleryId) {
    const data = this.getGalleryData(galleryId);
    if (!data) return;

    this.currentGallery = data;
    this.currentIndex = 0;
    this.previousFocus = document.activeElement;
    this.hadScrollLock = document.documentElement.hasAttribute('scroll-lock');

    this.title.textContent = data.title || 'Past Trip Gallery';
    this.destination.textContent = data.destination || '';
    this.renderThumbnails();
    this.renderImage();

    this.lightbox.hidden = false;
    document.documentElement.setAttribute('scroll-lock', '');
    document.addEventListener('keydown', this.onDocumentKeydown);
    this.dialog.focus();
  }

  close() {
    if (this.lightbox.hidden) return;

    this.lightbox.hidden = true;
    this.currentGallery = null;
    this.currentIndex = 0;
    document.removeEventListener('keydown', this.onDocumentKeydown);

    if (!this.hadScrollLock) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }
  }

  getGalleryData(galleryId) {
    const dataNode = Array.from(this.root.querySelectorAll('[data-gallery-data]')).find(
      (node) => node.dataset.galleryData === galleryId
    );
    if (!dataNode) return null;

    try {
      const data = JSON.parse(dataNode.textContent);
      data.images = Array.isArray(data.images) ? data.images.filter((image) => image.src) : [];
      return data;
    } catch (error) {
      console.error('Unable to parse past trips gallery data.', error);
      return null;
    }
  }

  renderImage() {
    const images = this.currentGallery?.images || [];
    const image = images[this.currentIndex];
    const hasMultipleImages = images.length > 1;

    this.previousButton.disabled = !hasMultipleImages;
    this.nextButton.disabled = !hasMultipleImages;

    if (!image) {
      this.image.hidden = true;
      this.image.removeAttribute('src');
      this.image.alt = '';
      this.placeholder.hidden = false;
      this.caption.textContent = '';
      this.updateCurrentThumb();
      return;
    }

    this.image.src = image.src;
    this.image.alt = image.alt || this.currentGallery.title || '';
    this.image.hidden = false;
    this.placeholder.hidden = true;
    this.caption.textContent = image.caption || '';
    this.updateCurrentThumb();
  }

  renderThumbnails() {
    const images = this.currentGallery?.images || [];
    this.thumbs.innerHTML = '';

    if (images.length <= 1) return;

    images.forEach((image, index) => {
      const button = document.createElement('button');
      const thumbnail = document.createElement('img');

      button.type = 'button';
      button.className = 'past-trips-lightbox__thumb';
      button.setAttribute('aria-label', `View image ${index + 1}`);
      button.addEventListener('click', () => {
        this.currentIndex = index;
        this.renderImage();
      });

      thumbnail.src = image.thumb || image.src;
      thumbnail.alt = '';
      thumbnail.loading = 'lazy';
      button.append(thumbnail);
      this.thumbs.append(button);
    });
  }

  updateCurrentThumb() {
    this.thumbs.querySelectorAll('.past-trips-lightbox__thumb').forEach((button, index) => {
      button.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
    });
  }

  showPrevious() {
    const images = this.currentGallery?.images || [];
    if (images.length <= 1) return;

    this.currentIndex = (this.currentIndex - 1 + images.length) % images.length;
    this.renderImage();
  }

  showNext() {
    const images = this.currentGallery?.images || [];
    if (images.length <= 1) return;

    this.currentIndex = (this.currentIndex + 1) % images.length;
    this.renderImage();
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showPrevious();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showNext();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  trapFocus(event) {
    const focusableElements = Array.from(
      this.dialog.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);

    if (!focusableElements.length) {
      event.preventDefault();
      this.dialog.focus();
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-past-trips-page]').forEach((root) => {
    new PastTripsGallery(root);
  });
});
